import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import WorkerAvailabilityCard from "../components/availability/WorkerAvailabilityCard";
import AddDayOffModalChakra from "../components/days-off/AddDayOffModalChakra";

const CATEGORIES = {
  holiday: { label: 'Holiday', color: 'bg-red-100 text-red-800 border-red-200' },
  vacation: { label: 'Vacation', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  personal: { label: 'Personal Day', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  sick: { label: 'Sick Day', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800 border-gray-200' }
};

export default function AvailabilityNew({ embedded = false }) {
  const toast = useToast();
  const timezoneDropdownRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Timezone state
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [currentTimezone, setCurrentTimezone] = useState("");
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);

  // Workers state
  const [workers, setWorkers] = useState([]);
  const [workersAvailability, setWorkersAvailability] = useState({});
  const [workerSearch, setWorkerSearch] = useState("");

  // Days off state
  const [allDaysOff, setAllDaysOff] = useState([]);
  const [showAddDayOffModal, setShowAddDayOffModal] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timezoneDropdownRef.current && !timezoneDropdownRef.current.contains(event.target)) {
        setShowTimezoneDropdown(false);
        setTimezoneSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const [userResponse, timezonesResponse, workersResponse] = await Promise.all([
        fetch("/api/current_user", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
        fetch("/api/timezones", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
        fetch("/api/schedule_openings/filter_options", {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        }),
      ]);

      const userData = await userResponse.json();
      const timezonesData = await timezonesResponse.json();
      const workersData = await workersResponse.json();

      if (userData.user) {
        setCurrentTimezone(userData.user.timezone);
        setSelectedTimezone(userData.user.timezone);
      }

      if (timezonesData.success) {
        setTimezones(timezonesData.data);
      }

      if (workersData.workers) {
        setWorkers(workersData.workers);
        await loadAllWorkersAvailability(workersData.workers, csrfToken);
        await loadAllDaysOff(workersData.workers, csrfToken);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast({
        title: "Error loading data",
        description: "Failed to load initial data. Please refresh the page.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllWorkersAvailability = async (workersList, csrfToken) => {
    const availabilityPromises = workersList.map(worker =>
      fetch(`/api/workers/${worker.id}/availabilities`, {
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      }).then(res => res.json())
    );

    const results = await Promise.all(availabilityPromises);

    const availabilityMap = {};
    results.forEach((result, index) => {
      availabilityMap[workersList[index].id] = result.availabilities || {};
    });

    setWorkersAvailability(availabilityMap);
  };

  const loadAllDaysOff = async (workersList, csrfToken) => {
    const daysOffPromises = workersList.map(worker =>
      fetch(`/api/workers/${worker.id}/days_off`, {
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      }).then(res => res.json())
    );

    const results = await Promise.all(daysOffPromises);

    const allDays = results.flatMap((result, index) =>
      result.days_off.map(dayOff => ({
        ...dayOff,
        worker_name: workersList[index].name
      }))
    );

    setAllDaysOff(allDays);
  };

  const handleTimezoneChange = async () => {
    if (selectedTimezone === currentTimezone) {
      return;
    }

    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch("/api/current_user/update_timezone", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ user: { timezone: selectedTimezone } }),
      });

      const data = await response.json();

      if (data.success) {
        setCurrentTimezone(selectedTimezone);
        toast({
          title: "Timezone updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to update timezone",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating timezone:", error);
      toast({
        title: "Error",
        description: "Failed to update timezone",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailable = async (workerId, currentAvailable) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/workers/${workerId}/toggle_availability`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ available: !currentAvailable }),
      });

      const data = await response.json();

      if (data.success) {
        setWorkers(workers.map(w =>
          w.id === workerId ? { ...w, available: data.worker.available } : w
        ));

        toast({
          title: `Worker ${data.worker.available ? 'enabled' : 'disabled'}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update worker availability",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast({
        title: "Error",
        description: "Failed to update worker availability",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddTimeSlot = async (workerId, dayOfWeek, slotData) => {
    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/workers/${workerId}/availabilities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          availability: {
            day_of_week: dayOfWeek,
            start_time: slotData.startTime,
            end_time: slotData.endTime,
            active: true,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        toast({
          title: "Time slot added",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to add time slot",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error adding time slot:", error);
      toast({
        title: "Error",
        description: "Failed to add time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTimeSlot = async (workerId, slotId, slotData) => {
    try {
      setSaving(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/availabilities/${slotId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          availability: {
            start_time: slotData.startTime,
            end_time: slotData.endTime,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        toast({
          title: "Time slot updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join(", ") || "Failed to update time slot",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating time slot:", error);
      toast({
        title: "Error",
        description: "Failed to update time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTimeSlot = async (workerId, slotId) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/availabilities/${slotId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await response.json();

      if (data.success) {
        const availResponse = await fetch(`/api/workers/${workerId}/availabilities`, {
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        });
        const availData = await availResponse.json();

        setWorkersAvailability({
          ...workersAvailability,
          [workerId]: availData.availabilities || {}
        });

        toast({
          title: "Time slot deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting time slot:", error);
      toast({
        title: "Error",
        description: "Failed to delete time slot",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddDayOff = async (formData) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const baseUserId = formData.applyToUserIds[0];

      const response = await fetch(`/api/workers/${baseUserId}/days_off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          user_ids: formData.applyToUserIds,
          day_off: {
            name: formData.name,
            category: formData.category,
            start_date: formData.startDate,
            end_date: formData.endDate,
            start_time: formData.isPartialDay ? formData.startTime : null,
            end_time: formData.isPartialDay ? formData.endTime : null,
            notes: formData.notes,
            active: true
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowAddDayOffModal(false);
        await loadAllDaysOff(workers, csrfToken);

        toast({
          title: "Day off added",
          description: data.count > 1 ? `Added for ${data.count} workers` : undefined,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.errors?.join('\n') || 'Failed to add day off',
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to add day off:', err);
      toast({
        title: "Error",
        description: "Failed to add day off",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDayOff = async (id) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/days_off/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      const data = await response.json();

      if (data.success) {
        await loadAllDaysOff(workers, csrfToken);
        toast({
          title: "Day off deleted",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to delete day off:', err);
      toast({
        title: "Error",
        description: "Failed to delete day off",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const filteredTimezones = timezones.filter(tz =>
    tz.label.toLowerCase().includes(timezoneSearch.toLowerCase()) ||
    tz.name.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  const getSelectedTimezoneLabel = () => {
    const tz = timezones.find(t => t.name === selectedTimezone);
    return tz ? tz.label : selectedTimezone;
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(workerSearch.toLowerCase()) ||
    worker.first_name?.toLowerCase().includes(workerSearch.toLowerCase()) ||
    worker.last_name?.toLowerCase().includes(workerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <Flex minH={embedded ? "240px" : "calc(100vh - 100px)"} align="center" justify="center">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box maxW={embedded ? "full" : "1400px"} mx="auto" px={embedded ? 0 : { base: 4, md: 6 }} py={embedded ? 0 : { base: 6, md: 8 }}>
      {!embedded && (
        <Box mb={8}>
          <Heading size={{ base: "lg", md: "xl" }} color="gray.800" mb={2}>
            Worker Availability
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Configure when your team is available for appointments
          </Text>
        </Box>
      )}

      {/* Timezone Section */}
      <Box
        mb={8}
        p={6}
        borderRadius="xl"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Heading size="sm" mb={4}>
          Timezone
        </Heading>
        <HStack spacing={3} align="start">
          <FormControl flex={1} position="relative" ref={timezoneDropdownRef}>
            <Input
              value={showTimezoneDropdown ? timezoneSearch : getSelectedTimezoneLabel()}
              onChange={(e) => {
                setTimezoneSearch(e.target.value);
                setShowTimezoneDropdown(true);
              }}
              onFocus={() => {
                setTimezoneSearch("");
                setShowTimezoneDropdown(true);
              }}
              placeholder="Search for a timezone..."
              size="lg"
              borderWidth="2px"
            />
            {showTimezoneDropdown && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                mt={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="lg"
                maxH="300px"
                overflowY="auto"
                zIndex={1000}
              >
                {filteredTimezones.length > 0 ? (
                  filteredTimezones.map((tz, index) => (
                    <Box
                      key={`${tz.name}-${index}`}
                      px={4}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: "blue.50" }}
                      bg={tz.name === selectedTimezone ? "blue.100" : "white"}
                      onClick={() => {
                        setSelectedTimezone(tz.name);
                        setShowTimezoneDropdown(false);
                        setTimezoneSearch("");
                      }}
                    >
                      <Text fontSize="sm">{tz.label}</Text>
                    </Box>
                  ))
                ) : (
                  <Box px={4} py={2}>
                    <Text fontSize="sm" color="gray.500">
                      No timezones found
                    </Text>
                  </Box>
                )}
              </Box>
            )}
          </FormControl>
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleTimezoneChange}
            isLoading={saving}
            isDisabled={selectedTimezone === currentTimezone}
          >
            Save
          </Button>
        </HStack>
      </Box>

      {/* Workers Section */}
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="sm">Workers</Heading>
          {workers.length > 0 && (
            <Input
              placeholder="Search workers..."
              value={workerSearch}
              onChange={(e) => setWorkerSearch(e.target.value)}
              maxW="300px"
              size="sm"
            />
          )}
        </Flex>

        {workers.length === 0 ? (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>
              No workers found. Please sync with Jobber first.
            </AlertDescription>
          </Alert>
        ) : filteredWorkers.length === 0 ? (
          <Box p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
            <Text color="gray.500">No workers found matching "{workerSearch}"</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            {filteredWorkers.map((worker) => (
              <WorkerAvailabilityCard
                key={worker.id}
                worker={worker}
                availability={workersAvailability[worker.id] || {}}
                onToggleAvailable={handleToggleAvailable}
                onAddSlot={handleAddTimeSlot}
                onUpdateSlot={handleUpdateTimeSlot}
                onDeleteSlot={handleDeleteTimeSlot}
                saving={saving}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Days Off Section */}
      <Box mt={8}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="sm">Days Off & Holidays</Heading>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={() => setShowAddDayOffModal(true)}
          >
            Add Day Off
          </Button>
        </Flex>

        {allDaysOff.length === 0 ? (
          <Box
            p={6}
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            textAlign="center"
            boxShadow="sm"
          >
            <Text color="gray.500">No days off scheduled</Text>
            <Button
              mt={3}
              size="sm"
              colorScheme="blue"
              variant="outline"
              leftIcon={<AddIcon />}
              onClick={() => setShowAddDayOffModal(true)}
            >
              Add Your First Day Off
            </Button>
          </Box>
        ) : (
          <VStack spacing={2} align="stretch">
            {allDaysOff.map((dayOff) => (
              <Flex
                key={dayOff.id}
                p={4}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                justify="space-between"
                align="center"
                boxShadow="sm"
                _hover={{ boxShadow: "md", borderColor: "gray.300" }}
                transition="all 0.2s"
              >
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold">{dayOff.name}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {dayOff.worker_name} • {dayOff.date_range_display}
                  </Text>
                </VStack>
                <IconButton
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  aria-label={`Delete day off ${dayOff.name}`}
                  onClick={() => handleDeleteDayOff(dayOff.id)}
                />
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      {/* Add Day Off Modal */}
      {showAddDayOffModal && (
        <AddDayOffModalChakra
          workers={workers.map(w => ({ id: w.id, name: w.name }))}
          categories={CATEGORIES}
          dayOff={null}
          onClose={() => setShowAddDayOffModal(false)}
          onSubmit={handleAddDayOff}
        />
      )}
    </Box>
  );
}
